// "Wat past bij ons gezin" (0023, docs/DESIGN.md §8.P): de teksten van de wizardstap, de
// instellingen, de afwijking per lid, de bijzondere dagen, "Verborgen omdat ..." en de
// eetwaarschuwingen. Apart bestand zodat app.nl.ts klein blijft; de sleutels staan in
// `appText.profile.*`. Alleen de webapp: de kiosk krijgt al gefilterde, voorgeformatteerde lijsten.
//
// Woordkeus (ONTWERP beslissingen 106 en 107): het gaat over gewoontes, nooit over nationaliteit of
// afkomst. Een voorstel vraagt ("Past dit bij jullie?"), zegt nooit "Omdat jullie Turks zijn".
// Bijzondere dagen staan standaard uit en gaan alleen aan met een tik. Je-vorm, korte zinnen,
// werkwoorden op knoppen.
//
// LET OP: consent.text, consent.check, selfConsent.text en selfConsent.check horen bij
// PROFILE_CONSENT_VERSION en CONSENT_TEXT_HASHES (packages/domain/src/profile/vocab.ts). Wijzig je
// ze, dan een nieuwe versie + hash; de test in apps/api/src/profileContract.test.ts bewaakt dat.
export const appProfileNl = {
    title: 'Wat past bij ons gezin',
    intro: 'Kies wat bij jullie past. Dan wordt het rustiger in de lijsten. Alles is optioneel.',
    privacyLine: 'Alleen ouders kunnen dit inzien en wijzigen. Gekoppelde familie ziet het nooit.',
    privacyLink: 'Privacyverklaring',
    /** Pagina /privacy (art. 13 AVG); bron: docs/PRIVACY-gezinsprofiel.md. */
    privacyPage: {
        title: 'Privacyverklaring',
        /** Link bovenaan: terug naar de app (review d-fs r2). */
        back: 'Terug naar de app',
        intro: 'Over "Wat past bij ons gezin": keuken, geloof, eten en bijzondere dagen. Kort en duidelijk.',
        sections: [
            {
                title: 'Waarvoor',
                body: 'We gebruiken jullie keuzes alleen om te kiezen welke opties je ziet: sjablonen, pictogrammen, recepten en bijzondere dagen. Niet voor reclame, niet voor profielen over jullie en niet om te delen.',
            },
            {
                title: 'Welke gegevens',
                body: 'Keukens en tradities, geloof (met de gewoontes en de mazhab die je koos), eetregels (zoals halal of vegetarisch), afwijkingen per gezinslid, "eet iets anders" bij een recept en de bijzondere dagen die je aanzette. Geloof, halal en koosjer, en soms een traditie, zijn bijzondere persoonsgegevens (AVG artikel 9).',
            },
            {
                title: 'Grondslag',
                body: 'Jouw uitdrukkelijke toestemming (AVG artikel 9 lid 2 onder a). Een ouder geeft die voor zichzelf en voor de kinderen onder 16; een volwassene met een eigen login kan zelf toestemming geven voor een eigen afwijking. Alles is optioneel: zonder toestemming werkt de app gewoon.',
            },
            {
                title: 'Wie het ziet',
                body: 'Alleen de ouders in jullie gezin zien de keuzes zelf. De tablet en de app tonen alleen wat erbij past; bij eten noemen we wie iets niet eet. Gekoppelde familie, gasten, deellinks, agenda-feeds, foutmeldingen en logbestanden krijgen de keuzes nooit. Bijzondere dagen komen alleen in een agenda-export als je dat apart aanzet.',
            },
            {
                title: 'Hoe lang',
                body: 'Zolang de toestemming geldt. Intrekken wist de keuzes direct; uit back-ups verdwijnen ze binnen 90 dagen. Het bewijs van de toestemming zelf (wie, wanneer, welke tekst, zonder inhoud) bewaren we tot 5 jaar na intrekken, of tot het gezin verwijderd wordt.',
            },
            {
                title: 'Je rechten',
                body: 'Je kunt je gegevens inzien en wijzigen in Instellingen · Wat past bij ons gezin, ze downloaden in Instellingen · Gegevens downloaden (elke ouder zijn eigen gegevens, de beheerder alles), je toestemming intrekken (dat wist alles) en vragen om wissen. Ben je het ergens niet mee eens, dan kun je een klacht indienen bij de Autoriteit Persoonsgegevens.',
            },
            {
                title: 'Verantwoordelijke en contact',
                body: 'AMUMIN B.V. is verantwoordelijk voor deze gegevens. Je eigen gegevens download je zelf in Instellingen. Voor vragen of wissen neem je contact op met AMUMIN B.V.; zolang het vaste privacy-adres hieronder nog ontbreekt, via de beheerder van jullie gezinsaccount. Een vast privacy-adres en de KvK-gegevens komen hier te staan voordat andere gezinnen zich kunnen aanmelden.',
            },
        ],
    },
    /** Settings-kaart en de uitnodiging voor bestaande gezinnen. */
    settingsBody: 'Keuken, geloof, eten en bijzondere dagen',
    saveBar: 'Wijzigingen opslaan',
    invite: {
        title: 'Wat past bij jullie gezin?',
        body: 'Kies keuken, geloof en eten, dan zie je vooral wat bij jullie past. Het hoeft niet.',
        start: 'Instellen',
        later: 'Niet nu',
        never: 'Nee, bedankt',
    },
    /** Kaart voor de andere ingelogde ouder(s) nadat iemand het profiel instelde. */
    otherParent: {
        title: 'Wat past bij jullie gezin',
        body: '{name} heeft ingesteld wat bij jullie gezin past: {what}. Klopt dit ook voor jou?',
        /** Huisgenoten, stel, woongroep, ouderen thuis, zorg: zonder "gezin" (golf 6b live r1). */
        titleNeutral: 'Wat past bij jullie',
        bodyNeutral: '{name} heeft ingesteld wat bij jullie past: {what}. Klopt dit ook voor jou?',
        view: 'Bekijken',
    },
    wizard: {
        title: 'Wat past bij jullie?',
        body: 'Kies alleen wat je wilt; de rest mag leeg blijven. Je kunt alles later aanpassen.',
        skip: 'Overslaan',
        skipQuestion: 'Sla deze vraag over',
        next: 'Volgende',
        done: 'Klaar',
        save: 'Opslaan',
        nothingSaved: 'Je koos niets, dus we slaan niets op.',
        step: '{n} van {total}',
        /** Eén teller in de wizard: "Stap 3 van 5 · vraag 1 van 4". */
        question: '{step} · vraag {n} van {total}',
    },
    traditions: {
        title: 'Keuken en tradities',
        body: 'Welke keukens en tradities horen bij jullie? Kies er gerust meer.',
        hint: 'Dit gaat over gewoontes, niet over nationaliteit. We onthouden alleen welke keukens jullie graag eten. We gebruiken het voor de volgorde van recepten en om feestdagen voor te stellen.',
        labels: {
            dutch: 'Nederlands',
            turkish: 'Turks',
            moroccan: 'Marokkaans',
            surinamese: 'Surinaams',
            antillean: 'Antilliaans',
            indonesian: 'Indisch / Indonesisch',
            moluccan: 'Moluks',
            italian: 'Italiaans',
            polish: 'Pools',
            middle_eastern: 'Midden-Oosters',
            other: 'Anders',
        },
        otherKitchens: {
            title: 'Welke keukens eten jullie graag?',
            more: 'Meer keukens',
        },
    },
    suggestion: {
        title: 'Past dit bij jullie?',
        body: 'Je kiest zelf. Je kunt het altijd aanpassen.',
        accept: 'Ja, overnemen',
        decline: 'Zelf kiezen',
        /** Mag de sets noemen, zet ze nooit aan. */
        days: 'Er zijn ook {sets} om toe te voegen. Die zet je zelf aan bij Bijzondere dagen.',
    },
    faiths: {
        title: 'Geloof',
        body: 'Dan zie je de bijpassende opties. Feestdagen kun je daarna zelf aanzetten.',
        none: 'Geen geloof',
        /** Uitleg per keuze: "Je ziet dan Bidden, Moskee(les) en Koranles". */
        shows: 'Je ziet dan {list}',
        /** Golf 6b: gewoontes bij het gekozen geloof, optioneel (niets voorgevinkt). */
        /**
         * Golf 6c (f2-14): de mazhab (rechtsschool), alleen bij Islam. Eén rustige, optionele vraag;
         * de hint noemt moskeeën, nooit een nationaliteit.
         */
        madhab: {
            title: 'Welke mazhab (rechtsschool) volgen jullie?',
            body: 'Hiermee stellen we onder meer de tijd van ikindi/asr goed in. Je kunt dit altijd wijzigen.',
            hint: 'Turkse moskeeën, zoals die van Diyanet en Milli Görüş, volgen de Hanafi-school. Marokkaanse moskeeën meestal de Maliki-school.',
            unknown: 'Weet ik niet / sla over',
            labels: { hanafi: 'Hanafi', shafii: "Shafi'i", maliki: 'Maliki', hanbali: 'Hanbali' },
        },
        practicesTitle: 'Wat hoort er bij jullie bij?',
        practicesHint: 'Vink aan wat jullie doen. Alleen dat zetten we klaar als afspraak, klusje of pictogram. Niets aanvinken mag ook.',
        /** Nog niets gekozen: neutraal, nooit voorgevinkt (review d-ux r1). */
        openHint: 'Kies je niets, dan staan Bidden en Moskeeles niet tussen de voorstellen (zoeken vindt ze wel). Kies jullie geloof als je ze wilt zien.',
        labels: {
            islam: 'Islam',
            christianity: 'Christendom',
            judaism: 'Jodendom',
            hinduism: 'Hindoeïsme',
            other: 'Een ander geloof',
        },
    },
    food: {
        title: 'Eten',
        body: 'Recepten en boodschappen die hier niet bij passen, zetten we uit het zicht. Je vindt ze altijd terug met zoeken of "Alles tonen".',
        labels: {
            halal: 'Halal',
            no_pork: 'Geen varkensvlees',
            no_beef: 'Geen rundvlees',
            vegetarian: 'Vegetarisch',
            vegan: 'Veganistisch',
            kosher: 'Koosjer',
            no_alcohol: 'Geen alcohol',
        },
    },
    days: {
        title: 'Bijzondere dagen in de agenda',
        optional: 'optioneel',
        body: 'Wil je extra dagen in de agenda? De Nederlandse feestdagen staan aan; de extra sets staan allemaal uit. Zet aan wat je wilt zien.',
        perDay: 'Kies per dag',
        more: 'Meer bijzondere dagen',
        next: 'Eerstvolgende: {dates}',
        noDates: 'De data voor dit jaar volgen nog.',
        expected: '(verwacht)',
        expectedNote: 'De precieze dag kan een dag verschuiven.',
        eveBefore: 'Begint de avond ervoor bij zonsondergang.',
        /** "vr 20 of za 21 mrt" (maanzicht, nog niet bekend). */
        eitherOr: '{a} of {b}',
        /** Golf 2: los label in de maand vóór een nog niet vaste dag. */
        expectedTag: 'verwacht',
        /** Golf 2: blad bij een nog niet vaste dag in de agenda. */
        sheet: {
            expected: 'Deze dag staat nog niet vast.',
            when: 'Valt op {dates}.',
            confirm: 'Dag bevestigen',
            confirmHint: 'Bevestig of verschuif hem bij Wat past bij ons gezin, Bijzondere dagen.',
        },
        /** Golf 2 (oprichter #2): de Nederlandse feestdagen als eerste set, standaard aan. */
        nl: {
            title: 'Nederlandse feestdagen',
            note: 'Voor iedereen. Staat standaard aan.',
            days: {
                nieuwjaarsdag: 'Nieuwjaarsdag',
                goede_vrijdag: 'Goede Vrijdag',
                pasen: 'Pasen',
                koningsdag: 'Koningsdag',
                bevrijdingsdag: 'Bevrijdingsdag',
                hemelvaartsdag: 'Hemelvaartsdag',
                pinksteren: 'Pinksteren',
                kerstmis: 'Kerstmis',
                moederdag: 'Moederdag',
                vaderdag: 'Vaderdag',
                sinterklaas: 'Sinterklaas',
                oudejaarsavond: 'Oudejaarsavond',
            },
        },
        /** Namen van dagen die per taal verschillen; ontbreekt een naam, dan de naam uit de set. */
        dayNames: {
            ramadan_start: 'Begin Ramadan',
            ramadan_end: 'Laatste dag Ramadan',
            eid_al_fitr: 'Eid al-Fitr (Suikerfeest)',
            eid_al_adha: 'Eid al-Adha (Offerfeest)',
            islamic_new_year: 'Islamitisch nieuwjaar',
            mawlid: 'Mawlid (Mevlid)',
        },
        /** Golf 2 (oprichter #1): welke kalender volgen jullie voor de islamitische dagen? */
        tradition: {
            title: 'Welke kalender volgen jullie?',
            body: 'Moskeeën kiezen de feestdagen niet allemaal op dezelfde manier.',
            options: {
                diyanet: 'Turks (Diyanet, vast rooster)',
                saudi: 'Saoedi-Arabië (maanzicht)',
                morocco: 'Marokko (maanzicht, bevestiging de avond ervoor)',
                own_mosque: 'Eigen moskee (±1 dag)',
            },
            hints: {
                diyanet: 'De data staan jaren vooruit vast.',
                saudi: 'Tot de aankondiging de avond ervoor zie je twee mogelijke dagen.',
                morocco: 'Valt vaak een dag later dan in Saoedi-Arabië. Tot de aankondiging zie je twee mogelijke dagen.',
                own_mosque: 'Je ziet het vaste rooster. Kiest jullie moskee een dag eerder of later, zet het dan hier goed.',
            },
            defaultNote: 'Standaard het vaste rooster van Diyanet. Kies gerust wat jullie moskee volgt.',
            confirmTitle: 'Al bekend?',
            confirmBody: 'Tik op de dag die jullie moskee koos. Dan staat hij vast in de agenda.',
            unknown: 'Nog niet bekend',
            confirmedNote: 'Vast in de agenda.',
        },
        sets: {
            tr_national: 'Turkse feestdagen',
            keti_koti: 'Keti Koti',
            indies_remembrance: 'Herdenking 15 augustus',
            id_independence: 'Onafhankelijkheidsdag Indonesië',
            it_national: 'Italiaanse feestdagen',
            pl_national: 'Poolse feestdagen',
            islamic: 'Islamitische feestdagen',
            christian: 'Christelijke dagen',
            jewish: 'Joodse feestdagen',
            hindu: 'Hindoeïstische feestdagen',
        },
        ics: {
            toggle: 'Ook bijzondere dagen in agenda-exports',
            warning: 'Je agenda-app (bijvoorbeeld Google) ziet dan deze dagen.',
        },
    },
    /** Ingrediëntvlaggen in een zin ("bevat varkensvlees"). */
    flags: {
        pork: 'varkensvlees',
        beef: 'rundvlees',
        meat: 'vlees',
        fish: 'vis',
        shellfish: 'schaal- en schelpdieren',
        dairy: 'zuivel',
        egg: 'ei',
        honey: 'honing',
        gelatin: 'gelatine',
        alcohol: 'alcohol',
    },
    kitchens: {
        dutch: 'Hollands',
        turkish: 'Turks',
        moroccan: 'Marokkaans',
        surinamese: 'Surinaams',
        antillean: 'Antilliaans',
        indonesian: 'Indisch',
        moluccan: 'Moluks',
        italian: 'Italiaans',
        polish: 'Pools',
        middle_eastern: 'Midden-Oosters',
        chinese: 'Chinees',
        indian: 'Indiaas',
        mexican: 'Mexicaans',
        greek: 'Grieks',
        west_african: 'West-Afrikaans',
        central_african: 'Centraal-Afrikaans',
        east_african: 'Oost-Afrikaans',
        american: 'Amerikaans',
    },
    member: {
        follows: 'Volgt het gezin',
        differs: 'Anders dan het gezin',
        reset: 'Weer het gezin volgen',
        /** Tweede regel onder de naam in de ledenlijst. */
        plus: 'Volgt het gezin, plus {what}',
        minus: 'Volgt het gezin, zonder {what}',
        faithDiffers: 'Geloof: {what}',
        /** Volwassene of 16+ zonder eigen login. */
        notPossible: 'Voor {name} kun je dit niet apart instellen. Dat kan alleen als {name} zelf toestemming geeft met een eigen login. Tot dan volgt {name} het gezin.',
        /** Volwassene of 16+ met eigen login. */
        askSelf: 'Vraag {name} om dit zelf in te stellen.',
        shareLink: 'Link delen',
    },
    consent: {
        /** Moet gelijk zijn aan PROFILE_CONSENT_VERSION (packages/domain/src/profile/vocab.ts). */
        version: '2026-09-25.4',
        title: 'Mag dit?',
        /** Eén punt per regel (\n): de UI toont een korte lijst (COGA). */
        text: 'Geloof, eetregels en keukens kunnen gevoelige gegevens zijn. We gebruiken ze alleen om te kiezen welke opties je ziet.\nAlleen ouders kunnen dit inzien en wijzigen. De tablet en de app laten zien wat erbij past; bij eten noemen we wie iets niet eet.\nBij sommige keuzes kun je extra onderdelen op het bord zetten. Iedereen in de kamer ziet die dan.\nWe delen niets met gekoppelde familie of in gedeelde lijsten. Bijzondere dagen komen alleen in de agenda of in een agenda-export als je dat zelf aanzet.\nIntrekken kan altijd en wist het direct. Uit back-ups verdwijnt het binnen 90 dagen.\nAMUMIN B.V. is verantwoordelijk voor deze gegevens. Je rechten staan in de privacyverklaring.',
        check: 'Ik geef toestemming voor mezelf en voor mijn kinderen onder 16.',
        given: 'Toestemming gegeven door {name} op {date}',
        /** Fijnslijpen: zonder bekende naam (geen "door  op"). */
        givenAt: 'Toestemming gegeven op {date}',
        renew: 'De uitleg is veranderd. Lees hem even en geef opnieuw toestemming.',
        withdraw: 'Toestemming intrekken en wissen',
        withdrawConfirm: 'We wissen keukens, geloof, eetregels en bijzondere dagen van jullie gezin. Daarna zie je weer alles. Doorgaan?',
        withdrawn: 'Gewist. Je ziet weer alles.',
    },
    /** Een volwassene (of 16+) geeft zelf toestemming voor een eigen afwijking. */
    selfConsent: {
        title: 'Klopt dit voor jou?',
        text: 'Je stelt voor jezelf in wat je gelooft of eet. We gebruiken het alleen om te kiezen welke opties je ziet, en bij eten noemen we je naam als iets niet bij je past. Alleen jij en de ouders in dit gezin zien het. Intrekken wist het direct.',
        check: 'Ik geef toestemming voor mezelf.',
        withdraw: 'Mijn toestemming intrekken',
    },
    /** Namen van de sets midden in een zin ("Er zijn ook islamitische feestdagen ..."). */
    daySetsInline: {
        tr_national: 'Turkse feestdagen',
        keti_koti: 'Keti Koti',
        indies_remembrance: 'de herdenking van 15 augustus',
        id_independence: 'de Onafhankelijkheidsdag van Indonesië',
        it_national: 'Italiaanse feestdagen',
        pl_national: 'Poolse feestdagen',
        islamic: 'islamitische feestdagen',
        christian: 'christelijke dagen',
        jewish: 'joodse feestdagen',
        hindu: 'hindoeïstische feestdagen',
    },
    hidden: {
        showAll: 'Alles tonen',
        showLess: 'Minder tonen',
        count: { one: '{count} optie verborgen', other: '{count} opties verborgen' },
        /** Kop boven een groep verborgen opties na "Alles tonen" (één reden per groep). */
        groupFood: 'Verborgen omdat jullie {rules}',
        groupFaith: 'Hoort bij een ander geloof',
        /** Per geloof een eigen groep: "Verschijnt als je Christendom kiest". */
        groupNotChosen: 'Verschijnt als je {what} kiest',
        /** Geloofsvraag nog open (oprichtersbesluit 2026-09-25). */
        groupFaithOpen: 'Verborgen tot jullie bij geloof iets kiezen',
        /** Golf 6b: geloof gekozen, gewoonte niet aangevinkt. */
        groupPractice: 'Niet aangevinkt bij geloof',
        practice: 'Verborgen omdat jullie {practice} niet aanvinkten bij geloof. Wijzig in Instellingen.',
        change: 'Wijzig',
        /** Reden bij een verborgen optie in de zoekresultaten. */
        faith: 'Hoort bij {faiths}. Jullie kozen iets anders.',
        faithNone: 'Verborgen omdat jullie bij geloof Geen geloof kozen. Wijzig in Instellingen.',
        notChosen: 'Verschijnt als je {what} kiest in Instellingen.',
        faithOpen: 'Verborgen tot jullie bij geloof iets kiezen. Wijzig in Instellingen.',
        food: 'Verborgen omdat jullie {rules}. Wijzig in Instellingen.',
        variant: 'Er staat al een variant van dit recept in de lijst.',
        /** "halal eten", gebruikt in `food` ("omdat jullie halal eten"). */
        rulePhrase: {
            halal: 'halal eten',
            no_pork: 'geen varkensvlees eten',
            no_beef: 'geen rundvlees eten',
            vegetarian: 'vegetarisch eten',
            vegan: 'veganistisch eten',
            kosher: 'koosjer eten',
            no_alcohol: 'geen alcohol gebruiken',
        },
    },
    warning: {
        /** Eerst wat er botst: "Hier zit rookworst in." / meervoud "Hier zitten spekjes in." */
        contains: 'Hier zit {ingredient} in.',
        containsMany: 'Hier zitten {ingredient} in.',
        /** Eén persoon: "Yakub eet halal." */
        person: {
            halal: '{names} eet halal.',
            no_pork: '{names} eet geen varkensvlees.',
            no_beef: '{names} eet geen rundvlees.',
            vegetarian: '{names} eet vegetarisch.',
            vegan: '{names} eet veganistisch.',
            kosher: '{names} eet koosjer.',
            no_alcohol: '{names} gebruikt geen alcohol.',
        },
        /** Meer namen: "Anne en Yakub eten geen varkensvlees." */
        people: {
            halal: '{names} eten halal.',
            no_pork: '{names} eten geen varkensvlees.',
            no_beef: '{names} eten geen rundvlees.',
            vegetarian: '{names} eten vegetarisch.',
            vegan: '{names} eten veganistisch.',
            kosher: '{names} eten koosjer.',
            no_alcohol: '{names} gebruiken geen alcohol.',
        },
        /** Iedereen aan tafel: "Jullie eten halal." */
        everyone: {
            halal: 'Jullie eten halal.',
            no_pork: 'Jullie eten geen varkensvlees.',
            no_beef: 'Jullie eten geen rundvlees.',
            vegetarian: 'Jullie eten vegetarisch.',
            vegan: 'Jullie eten veganistisch.',
            kosher: 'Jullie eten koosjer.',
            no_alcohol: 'Jullie gebruiken geen alcohol.',
        },
        check: 'Let op: dit bevat misschien {what}.',
        /** Koosjer: vlees en zuivel samen in één gerecht (review d-reg r3). */
        meatDairy: 'Hier zit vlees met zuivel in: {meat} en {dairy}.',
        /** Eén boodschap: zeg wat het probleem is, niet de naam ("Hier zit Spekkies in"). */
        item: {
            pork: 'Hier zit varkensvlees in.',
            beef: 'Hier zit rundvlees in.',
            meat: 'Hier zit vlees in.',
            fish: 'Hier zit vis in.',
            shellfish: 'Hier zitten schaal- of schelpdieren in.',
            dairy: 'Hier zit zuivel in.',
            egg: 'Hier zit ei in.',
            honey: 'Hier zit honing in.',
            gelatin: 'Hier zit meestal varkensgelatine in.',
            alcohol: 'Hier zit alcohol in.',
        },
        swap: 'Tip: {swap} in plaats van {ingredient}.',
        swapButton: 'Ruilen',
        /** Het gerecht of de boodschap staat er al: de knop laat het staan (review d-ux r1). */
        addAnyway: 'Zo laten',
        add: 'Oké',
        eatsOther: '{name} eet iets anders',
        whoEats: 'Wie eet mee?',
        everyoneEats: 'Iedereen',
        and: 'en',
    },
    /**
     * Schermen van builder A (wizardstap, Instellingen, ledenblad, kaarten op Vandaag). Geen
     * toestemmingstekst: die staat in `consent`/`selfConsent` (met versie en hash).
     */
    screens: {
        /** Wat een geloof zichtbaar maakt ("Je ziet dan Bidden, Moskee(les) en Koranles"). */
        practices: {
            prayer: 'Bidden',
            wudu: 'Wudu doen',
            mosque: 'Moskee(les)',
            quran: 'Koranles',
            church: 'Kerk',
            shabbat: 'Sjabbat',
            synagogue: 'Synagoge',
            mandir: 'Mandir',
        },
        noneHint: 'Dan zetten we geloofsopties zoals Bidden en Moskeeles uit het zicht.',
        otherFaithHint: 'Bidden blijft zichtbaar; de opties van de genoemde geloven niet.',
        stepLabel: 'Stap {n} van {total}',
        progress: 'Vraag {n} van {total}',
        noConsentYet: 'Zonder toestemming slaan we niets op.',
        saved: 'Opgeslagen. Je ziet vooral wat bij jullie past.',
        savedMember: 'Opgeslagen voor {name}.',
        resetMember: '{name} volgt weer het gezin.',
        /** Je eigen rij (16+ of volwassene): niet in de derde persoon. */
        savedSelf: 'Opgeslagen.',
        resetSelf: 'Je volgt weer het gezin.',
        selfWithdrawn: 'Je eigen keuzes zijn gewist.',
        noChanges: 'Er is niets veranderd.',
        loadError: 'Dit kon niet geladen worden.',
        changed: 'Je hebt iets veranderd.',
        settingsCardBody: 'Keuken, geloof, eten en bijzondere dagen',
        membersTitle: 'Per gezinslid',
        membersIntro: 'Het gezin is de standaard. Een gezinslid kan afwijken.',
        consentTitle: 'Toestemming',
        appliesTo: 'Geldt voor: {list}',
        selfTag: '{name} (zelf)',
        kidsTag: '{list} (kinderen onder 16)',
        noConsent: 'Er is nog geen toestemming gegeven. Die vragen we bij de eerste keer opslaan.',
        inviteLater: 'We vragen het later nog een keer.',
        inviteNever: 'Prima. Je vindt het altijd in Instellingen.',
        otherParentWhat: 'keuken, geloof en eten',
        dayCount: 'dag {n} van {total}',
        daysOn: 'Aan',
        daysOff: 'Uit',
        lessDays: 'Minder bijzondere dagen',
        offeredEmpty: 'Kies hierboven keuken of geloof, dan stellen we passende dagen voor.',
        member: {
            title: 'Aanpassen',
            open: 'Aanpassen voor {name}',
            faith: 'Geloof',
            food: 'Eten',
            foodHint: 'De keuzes van het gezin staan aangevinkt. Wat je aanzet komt erbij, wat je uitzet geldt niet voor {name}. Latere keuzes van het gezin volgen vanzelf.',
            faithFollows: 'Geloof: zoals het gezin',
            lapsed: '{name} stelt dit nu zelf in.',
            selfIntro: 'Je stelt dit in voor jezelf.',
            selfGiven: 'Je gaf toestemming voor jezelf.',
            withdrawSelfTitle: 'Je eigen keuze wissen?',
            withdrawSelfBody: 'Je volgt dan weer het gezin. Wat je voor jezelf instelde, wissen we direct.',
            withdrawSelfConfirm: 'Wissen',
            save: 'Opslaan',
            linkCopied: 'Link gekopieerd.',
            notSetYet: 'Stel eerst in wat bij het gezin past en sla het op. Daarna kun je per gezinslid afwijken.',
        },
        /** Een login zonder ouderrol (kind van 16+ met eigen login): alleen de eigen keuzes. */
        self: {
            title: 'Wat past bij mij',
            intro: 'Geloof of eet je anders dan het gezin? Dat stel je hier zelf in. Alleen jij en je ouders zien het.',
            parents: 'Dit stellen je ouders in. Vanaf je 16e kun je hier zelf aangeven wat voor jou anders is.',
            notYet: 'Je ouders hebben nog niet ingesteld wat bij jullie gezin past. Daarna kun je hier je eigen keuzes maken.',
            settingsBody: 'Je eigen geloof en eten',
        },
    },
};
//# sourceMappingURL=profileApp.nl.js.map